// src/views/auth/passwordResetShared.js
// --- UPDATE (Forgot / reset password) -----------------------------------------
// Shared pieces for /auth/forgot-password and /auth/reset-password:
//   AuthPageLayout      same two-column FlowSense MOW+ layout as the sign-in page
//   PasswordStrength    live strength bar + the server's rules as a checklist
//   checkPassword()     the same rules the server enforces (services/passwordResetService.js)
//   authApi()           fetch helper for the public /auth endpoints (JSON in / out, status kept)
// --- END UPDATE ---------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import {
    Box, Flex, HStack, VStack, Text, Heading, Icon, Badge, Progress, SimpleGrid, useColorModeValue,
} from '@chakra-ui/react';
import { MdWaterDrop, MdAutoAwesome, MdCheckCircle, MdRadioButtonUnchecked, MdLockReset, MdMarkEmailRead, MdShield } from 'react-icons/md';
import illustration from '../../assets/img/auth/auth.png';
import { config } from '../../config';

export const MIN_LENGTH_DEFAULT = 8;
const COMMON = new Set([
    'password', 'password1', 'password123', '12345678', '123456789', '1234567890', 'qwerty123', 'qwertyuiop',
    'iloveyou', 'admin123', 'administrator', 'welcome1', 'welcome123', 'letmein1', 'changeme', 'changeme1',
    'flowsense', 'flowsense1', 'flowsense123', 'bahamas1', 'bahamas123', 'nassau123', 'mow12345', 'abc12345',
]);

const byteLength = (s) => {
    try { return new TextEncoder().encode(s).length; } catch { return s.length; }
};

/** Same rules as the server. Returns { rules: [{ key, label, ok }], ok, score 0-4, label, color }. */
export function checkPassword(password, { minLength = MIN_LENGTH_DEFAULT, confirm } = {}) {
    const p = password || '';
    const rules = [
        { key: 'length', label: `At least ${minLength} characters`, ok: p.length >= minLength && byteLength(p) <= 72 },
        { key: 'mix', label: 'A letter and a number', ok: /[A-Za-z]/.test(p) && /\d/.test(p) },
        { key: 'common', label: 'Not a common password', ok: p.length > 0 && !COMMON.has(p.toLowerCase()) },
        { key: 'spaces', label: 'No space at the start or end', ok: p.length > 0 && !/^\s|\s$/.test(p) },
    ];
    if (confirm !== undefined) rules.push({ key: 'match', label: 'Both passwords match', ok: p.length > 0 && p === confirm });
    let score = 0;
    if (p.length >= minLength) score += 1;
    if (p.length >= minLength + 4) score += 1;
    if (/[a-z]/.test(p) && /[A-Z]/.test(p)) score += 1;
    if (/\d/.test(p) && /[^A-Za-z0-9]/.test(p)) score += 1;
    if (!rules.slice(0, 4).every((r) => r.ok)) score = Math.min(score, 1);
    const levels = [
        { label: 'Too weak', color: 'red' }, { label: 'Weak', color: 'orange' }, { label: 'Fair', color: 'yellow' },
        { label: 'Good', color: 'teal' }, { label: 'Strong', color: 'green' },
    ];
    return { rules, ok: rules.every((r) => r.ok), score, ...levels[score] };
}

/** POST / GET a public auth endpoint; throws Error with .status / .code / .problems / .retryAfter. */
export async function authApi(path, { method = 'GET', body, signal } = {}) {
    const res = await fetch(`${config.apiUrl}/auth${path}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
        signal,
    });
    let data = {};
    try { data = await res.json(); } catch { data = {}; }
    if (!res.ok) {
        const msg = data.message || data.errors?.[0]?.msg || `Request failed (${res.status})`;
        throw Object.assign(new Error(msg), { status: res.status, code: data.code, problems: data.problems, retryAfter: Number(res.headers.get('Retry-After')) || data.retry_after_s || null, data });
    }
    return data;
}

export function PasswordStrength({ password, confirm, minLength }) {
    const muted = useColorModeValue('gray.500', 'gray.400');
    const r = checkPassword(password, { minLength, confirm });
    return (
        <Box aria-live="polite">
            <HStack justify="space-between" mb={1}>
                <Text fontSize="xs" color={muted}>Password strength</Text>
                <Text fontSize="xs" fontWeight="700" color={password ? `${r.color}.400` : muted} data-testid="strength-label">{password ? r.label : '—'}</Text>
            </HStack>
            <Progress value={password ? ((r.score + 1) / 5) * 100 : 0} size="xs" borderRadius="full" colorScheme={r.color} aria-label="Password strength" />
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={1} mt={2}>
                {r.rules.map((rule) => (
                    <HStack key={rule.key} spacing={1.5} color={rule.ok ? 'green.400' : muted} data-testid={`rule-${rule.key}`} data-ok={rule.ok ? 'true' : 'false'}>
                        <Icon as={rule.ok ? MdCheckCircle : MdRadioButtonUnchecked} boxSize={3.5} />
                        <Text fontSize="xs">{rule.label}</Text>
                    </HStack>
                ))}
            </SimpleGrid>
        </Box>
    );
}
PasswordStrength.propTypes = { password: PropTypes.string, confirm: PropTypes.string, minLength: PropTypes.number };

const STEPS = [
    { icon: MdMarkEmailRead, title: 'Get the link', text: 'We email a reset link to the address on your account.' },
    { icon: MdLockReset, title: 'Choose a new password', text: 'The link works once and expires after 30 minutes.' },
    { icon: MdShield, title: 'Stay secure', text: 'You get a confirmation email whenever your password changes.' },
];

/** Same look as the sign-in page: form on the left, brand panel on the right. */
export function AuthPageLayout({ children, panelTitle = 'Back into FlowSense in a minute.', panelText = 'Reset your password yourself — no ticket needed. Works for the web dashboard and the FlowSense Tech app.' }) {
    const textColor = useColorModeValue('gray.800', 'white');
    const textColorSecondary = useColorModeValue('gray.500', 'gray.400');
    const textColorBrand = useColorModeValue('blue.600', 'blue.300');
    const authBg = useColorModeValue('white', 'gray.900');
    return (
        <Flex position="relative" minH="100vh" w="100%" bg={authBg}>
            <Flex direction="column" w={{ base: '100%', md: '50%', lg: '45%' }} px={{ base: 6, md: 12, xl: 20 }} py={{ base: 10, md: 16 }} justifyContent="center" alignItems="center" overflowY="auto">
                <Box w="100%" maxW="420px">
                    <HStack spacing={3} mb={8}>
                        <Flex w="44px" h="44px" borderRadius="xl" bgGradient="linear(to-br, blue.500, purple.600)" align="center" justify="center" boxShadow="md">
                            <Icon as={MdWaterDrop} color="white" boxSize={6} />
                        </Flex>
                        <Box>
                            <Text fontWeight="900" fontSize="lg" color={textColor} lineHeight="1.1">FlowSense <Text as="span" color={textColorBrand}>MOW+</Text></Text>
                            <Text fontSize="xs" color={textColorSecondary}>Utility operations platform</Text>
                        </Box>
                    </HStack>
                    {children}
                    <Text mt={8} fontSize="xs" color={textColorSecondary} textAlign="center">© {new Date().getFullYear()} FlowSense MOW+ · Authorised users only</Text>
                </Box>
            </Flex>
            <Flex w={{ base: '0%', md: '50%', lg: '55%' }} display={{ base: 'none', md: 'flex' }} position="relative" bgImage={`url(${illustration})`} bgSize="cover" bgPosition="center" borderBottomLeftRadius={{ lg: '120px', xl: '200px' }} overflow="hidden">
                <Box position="absolute" inset={0} bgGradient="linear(to-br, rgba(30,64,175,0.92), rgba(88,28,135,0.88))" />
                <Flex position="relative" direction="column" justify="center" px={{ md: 10, xl: 20 }} py={16} color="white" w="100%">
                    <Badge alignSelf="flex-start" bg="whiteAlpha.200" color="white" px={3} py={1} borderRadius="full" mb={5} display="flex" alignItems="center" gap={1}>
                        <Icon as={MdAutoAwesome} /> Account security
                    </Badge>
                    <Heading fontSize={{ md: '3xl', xl: '4xl' }} fontWeight="900" lineHeight="1.15" mb={4} maxW="520px">{panelTitle}</Heading>
                    <Text fontSize="md" color="whiteAlpha.800" mb={10} maxW="480px">{panelText}</Text>
                    <VStack align="stretch" spacing={3} maxW="480px">
                        {STEPS.map((s, i) => (
                            <HStack key={s.title} align="flex-start" spacing={3} p={4} bg="whiteAlpha.100" borderRadius="xl" borderWidth="1px" borderColor="whiteAlpha.200">
                                <Flex w="36px" h="36px" borderRadius="lg" bg="whiteAlpha.200" align="center" justify="center" flexShrink={0}><Icon as={s.icon} boxSize={5} /></Flex>
                                <Box>
                                    <Text fontWeight="800" fontSize="sm">{i + 1}. {s.title}</Text>
                                    <Text fontSize="xs" color="whiteAlpha.800">{s.text}</Text>
                                </Box>
                            </HStack>
                        ))}
                    </VStack>
                </Flex>
            </Flex>
        </Flex>
    );
}
AuthPageLayout.propTypes = { children: PropTypes.node, panelTitle: PropTypes.string, panelText: PropTypes.string };
